// ============================================================
// AN OBJECT THAT COUNTS AS TERRAIN (2026-10-26, board 6 of the Cover & Objects canvas).
//
// When its sheet says so, an Object's body is an exact BOX on the map - w x d x h metres, of its material, turned with
// its token's heading - fed to sight, cover and movement like any other solid: what you see is what blocks. It is
// DERIVED from the roster and the tokens, never stored as terrain (the rulings: `terrainSolids` derives it), so an
// undo, a drag or a heal moves it with no write of its own.
//
//   - intact, damaged, critical: the box, tinted by its team, darker as it is hurt;
//   - destroyed: RUBBLE (the same footprint, 1 m tall, sight passes, low cover, difficult ground) or nothing;
//   - GM-hidden: a `hidden` solid, which only a GM's screen draws (a player's screen has no token for it at all);
//   - too big for the map: skipped, and named, so the map can say why.
//
// Every check about the object's OWN place - sight to it, its overlap note, its own drag - skips its own box
// (`withoutOwnBox`): a thing is not in cover behind itself.
//
// Pure. Zero React dependency.
// ============================================================
import { boundaryExtent, type Boundary } from '../battlefield/boundary';
import { makeSolid } from '../battlefield/solids';
import { objectSolidId } from '../battlefield/ownBox';
import { DEFAULT_MATERIALS, derivePassability, materialAp } from '../battlefield/voxels';
import type { Token } from '../battlefield/tokens';
import type { Solid } from '../battlefield/types';
import { objectState, type ObjectState } from '../plate/objectPlate';
import { projectDefender } from '../roster/projection';
import { isHexLivery, isTeamLivery, liveryByCombatant, TEAM_LIVERIES, type Livery } from '../roster/team';
import type { Combatant } from '../roster/types';
import { readObjectSpec, terrainBoxOf, type ObjectSpec } from './objectSpec';

/**
 * The team colours as hex, for the scene's face shading (which works in hex). They are `--livery-<name>` in
 * theme.css; a test holds the two equal.
 */
export const LIVERY_HEX: Record<(typeof TEAM_LIVERIES)[number] | 'none', string> = {
  steel: '#d9dde2',
  cobalt: '#60a5fa',
  verdigris: '#2dd4bf',
  magenta: '#f472b6',
  violet: '#a78bfa',
  none: '#8a9099',
};

/** How dark a hurt box is drawn: the faces darken by band (board 6). */
const SHADE: Record<Exclude<ObjectState, 'destroyed'>, number> = { intact: 1, damaged: 0.78, critical: 0.56 };
const RUBBLE_HEIGHT_M = 1;

export interface ObjectSolidsInput {
  roster: readonly Combatant[];
  tokens: Readonly<Record<string, Token | undefined>>;
  boundary?: Boundary | null;
  /** The GM's hidden figures: their boxes are hidden solids. */
  hiddenIds?: ReadonlySet<string>;
}

export interface ObjectSolids {
  solids: Solid[];
  /** The objects whose box is larger than the map, by combatant id: drawn as marks, with TOO_BIG_NOTE. */
  tooBig: string[];
}

function darken(hex: string, by: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const ch = (shift: number) => Math.round(((n >> shift) & 255) * by);
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The team's colour as hex, as the roster resolves it (`liveryByCombatant`: the colour a team picked, else the one
 * the palette gives it) - so the box wears what its mark and plate wear (the review); the no-team grey for none.
 */
function liveryHex(livery: Livery | undefined): string {
  if (isHexLivery(livery)) return livery.toLowerCase();
  return isTeamLivery(livery) ? LIVERY_HEX[livery] : LIVERY_HEX.none;
}

/** Does the box fit the arena's extent, either way round? */
function fits(box: NonNullable<ObjectSpec['box']>, boundary: Boundary | null | undefined): boolean {
  if (!boundary) return true;
  const { min, max } = boundaryExtent(boundary);
  const [w, d] = [max.x - min.x, max.z - min.z];
  return (box.w <= w && box.d <= d) || (box.w <= d && box.d <= w);
}

interface Placed {
  c: Combatant;
  token: Token;
  box: NonNullable<ObjectSpec['box']>;
  spec: ObjectSpec;
  hidden: boolean;
  /** Its team's colour, as hex. */
  colour: string;
}

/** The box itself, or the rubble it left: one solid at the token, turned with its heading. */
function solidOf({ c, token, box, spec, hidden, colour: team }: Placed, state: ObjectState): Solid {
  const rubble = state === 'destroyed';
  const material = DEFAULT_MATERIALS[rubble ? 'rubble' : spec.material] ?? DEFAULT_MATERIALS.stone;
  const height = rubble ? RUBBLE_HEIGHT_M : box.h;
  const colour = rubble ? material.colour : darken(team, SHADE[state as keyof typeof SHADE]);
  return makeSolid({
    id: objectSolidId(c.id),
    sides: 4,
    radiusBottom: 0.5,
    radiusTop: 0.5,
    scaleX: box.w,
    scaleZ: box.d,
    height,
    rotation: token.heading ?? 0,
    position: { x: token.x, y: token.elevation, z: token.z },
    // Rubble is low cover you can see over and across (the rulings): explicit, since a 1 m lump would derive more.
    ...(rubble
      ? { cover: { level: 'low', ap: materialAp(material) }, coverMode: 'explicit', transparency: 2, opaque: false }
      : { cover: { level: 'none', ap: materialAp(material) }, coverMode: 'derived', ...seeThrough(material) }),
    colour,
    terrain: rubble ? 'difficult' : derivePassability(height, material.permeability ?? 0),
    label: c.name || c.sheet.name,
    ...(hidden ? { hidden: true } : {}),
  });
}

const seeThrough = (m: { transparency: 0 | 1 | 2 }) => ({ transparency: m.transparency, opaque: m.transparency === 0 });

/** The terrain objects on the map, with where they stand; the rest are marks only. */
function placedBoxes({ roster, tokens, hiddenIds }: ObjectSolidsInput): Placed[] {
  const boxed = roster.filter((c) => tokens[c.id] && terrainBoxOf(c.sheet));
  if (!boxed.length) return [];
  const liveries = liveryByCombatant(roster);
  return boxed.map((c) => ({
    c,
    token: tokens[c.id] as Token,
    box: terrainBoxOf(c.sheet)!,
    spec: readObjectSpec(c.sheet.objectSpec),
    hidden: !!hiddenIds?.has(c.id),
    colour: liveryHex(liveries[c.id]),
  }));
}

/** Every terrain object's solid, and the names of those too big to be drawn as one. */
export function objectSolids(input: ObjectSolidsInput): ObjectSolids {
  const out: ObjectSolids = { solids: [], tooBig: [] };
  for (const placed of placedBoxes(input)) {
    if (!fits(placed.box, input.boundary)) {
      out.tooBig.push(placed.c.id);
      continue;
    }
    const state = objectState(projectDefender(placed.c));
    if (state !== 'destroyed' || placed.spec.remains === 'rubble') out.solids.push(solidOf(placed, state));
  }
  return out;
}

/**
 * What every box depends on, as one string: a memo key that changes only when an object's box would, so a figure
 * walking elsewhere does not rebuild the scene's solids.
 */
export function objectDigest(input: ObjectSolidsInput): string {
  const boxes = placedBoxes(input);
  if (!boxes.length) return '';
  // The map too: it decides which boxes are too big for it (the review).
  const map = JSON.stringify(input.boundary ?? null);
  const each = boxes.map(({ c, token, spec, hidden, colour }) => {
    const at = [token.x, token.z, token.elevation, token.heading ?? 0].join(',');
    return `${c.id}@${at}:${JSON.stringify(spec)}:${objectState(projectDefender(c))}:${colour}:${hidden}`;
  });
  return [map, ...each].join('|');
}

/** What the map says under an object whose box it cannot hold. */
export const TOO_BIG_NOTE = 'Too large for this map';

/** The terrain's solids with the objects' boxes after them; the terrain's own array when there are none. */
export function withObjectSolids(terrain: Solid[], objects: ObjectSolids): Solid[] {
  return objects.solids.length ? [...terrain, ...objects.solids] : terrain;
}
